/**
 * NOS-157: Theme package loading, validation, and application (spec §B1).
 *
 * A theme is data, not code — see src/types/theme.ts. This module has three
 * layers:
 *   1. validateThemePackage()      — structural + registry checks, pure
 *   2. buildSeedRows() /
 *      planThemeApplication()      — pure planners (unit-testable, no I/O)
 *   3. applyThemeAtSignup()        — thin supabase wrapper over the planners
 *
 * Apply semantics (locked in spec): signup seeds pages from the package;
 * post-signup switching applies tokens + variants but NEVER touches user
 * content — sections map by type, unmatched sections keep their current look.
 */
import { supabase } from "@/lib/supabase"
import type { Json } from "@/types/database"
import type {
  ThemePackage,
  ThemePackagePage,
  ThemePackageValidation,
} from "@/types/theme"
import type { StoredDesignTokens } from "@/lib/designTokens"
import { legacyColumnsFromTokens } from "@/lib/designTokens"
import { getDefaultBlockContent, getRegisteredBlockTypes } from "@/components/website-builder/blocks/registry"
import { BLOCK_VARIANTS } from "@/components/blocks/render/variants"
import type { BlockType } from "@/components/website-builder/blocks/types"

// ─── Validation ──────────────────────────────────────────────────────────────

const TYPE_SCALES = ["compact", "regular", "display"]
const SECTION_SPACINGS = ["compact", "regular", "airy"]
const RADII = ["sharp", "soft", "round"]
const BUTTON_STYLES = ["solid", "outline", "pill"]
const ELEVATIONS = ["flat", "soft", "layered"]
const HEX_RE = /^#[0-9a-fA-F]{6}$/

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

function validateTokens(tokens: unknown, errors: string[]): void {
  if (!isRecord(tokens)) {
    errors.push("tokens must be an object")
    return
  }
  const colors = tokens.colors
  if (colors !== undefined) {
    if (!isRecord(colors)) {
      errors.push("tokens.colors must be an object")
    } else {
      for (const key of ["primary", "secondary", "accent"]) {
        const v = colors[key]
        if (v !== undefined && (typeof v !== "string" || !HEX_RE.test(v))) {
          errors.push(`tokens.colors.${key} must be a #rrggbb hex string`)
        }
      }
    }
  }
  const typography = tokens.typography
  if (typography !== undefined) {
    if (!isRecord(typography)) {
      errors.push("tokens.typography must be an object")
    } else if (typography.scale !== undefined && !TYPE_SCALES.includes(typography.scale as string)) {
      errors.push(`tokens.typography.scale must be one of ${TYPE_SCALES.join("/")}`)
    }
  }
  const spacing = tokens.spacing
  if (isRecord(spacing) && spacing.sectionY !== undefined && !SECTION_SPACINGS.includes(spacing.sectionY as string)) {
    errors.push(`tokens.spacing.sectionY must be one of ${SECTION_SPACINGS.join("/")}`)
  }
  const shape = tokens.shape
  if (isRecord(shape)) {
    if (shape.radius !== undefined && !RADII.includes(shape.radius as string)) {
      errors.push(`tokens.shape.radius must be one of ${RADII.join("/")}`)
    }
    if (shape.buttonStyle !== undefined && !BUTTON_STYLES.includes(shape.buttonStyle as string)) {
      errors.push(`tokens.shape.buttonStyle must be one of ${BUTTON_STYLES.join("/")}`)
    }
  }
  if (tokens.elevation !== undefined && !ELEVATIONS.includes(tokens.elevation as string)) {
    errors.push(`tokens.elevation must be one of ${ELEVATIONS.join("/")}`)
  }
}

/**
 * Validate an untrusted value as a ThemePackage. Checks structure, that every
 * section type exists in the blocks registry, and that every variant id exists
 * in BLOCK_VARIANTS for its type. Returns all errors, not just the first.
 */
export function validateThemePackage(input: unknown): ThemePackageValidation {
  const errors: string[] = []
  if (!isRecord(input)) {
    return { ok: false, errors: ["package must be a JSON object"] }
  }

  const meta = input.meta
  if (!isRecord(meta) || typeof meta.name !== "string" || meta.name.trim() === "") {
    errors.push("meta.name is required")
  }

  if (input.tokens !== undefined) validateTokens(input.tokens, errors)

  const registered = new Set<string>(getRegisteredBlockTypes())
  const pages = input.pages
  // Empty pages is legal: tokens-only packages restyle without defining layouts
  // (signup seeding falls back to the default structure at the call site).
  if (!Array.isArray(pages)) {
    errors.push("pages must be an array")
  } else {
    const slugs = new Set<string>()
    pages.forEach((page, pi) => {
      if (!isRecord(page)) {
        errors.push(`pages[${pi}] must be an object`)
        return
      }
      for (const key of ["slug", "title", "page_type"]) {
        if (typeof page[key] !== "string" || (page[key] as string).trim() === "") {
          errors.push(`pages[${pi}].${key} is required`)
        }
      }
      if (typeof page.slug === "string") {
        if (slugs.has(page.slug)) errors.push(`pages[${pi}].slug "${page.slug}" is duplicated`)
        slugs.add(page.slug)
      }
      const sections = page.sections
      if (!Array.isArray(sections)) {
        errors.push(`pages[${pi}].sections must be an array`)
        return
      }
      sections.forEach((section, si) => {
        if (!isRecord(section)) {
          errors.push(`pages[${pi}].sections[${si}] must be an object`)
          return
        }
        const type = section.type
        if (typeof type !== "string" || !registered.has(type)) {
          errors.push(`pages[${pi}].sections[${si}].type "${String(type)}" is not a registered block type`)
          return
        }
        const variant = section.variant
        if (variant !== undefined) {
          const defs = BLOCK_VARIANTS[type as BlockType]
          const known = defs?.some((d) => d.id === variant)
          if (!known) {
            errors.push(
              `pages[${pi}].sections[${si}].variant "${String(variant)}" is not a registered variant for "${type}"`
            )
          }
        }
        if (section.content !== undefined && !isRecord(section.content)) {
          errors.push(`pages[${pi}].sections[${si}].content must be an object`)
        }
      })
    })
  }

  if (input.imagery !== undefined) {
    const imagery = input.imagery
    if (!isRecord(imagery)) {
      errors.push("imagery must be an object")
    } else if (
      imagery.unsplash_queries !== undefined &&
      (!Array.isArray(imagery.unsplash_queries) || imagery.unsplash_queries.some((q) => typeof q !== "string"))
    ) {
      errors.push("imagery.unsplash_queries must be an array of strings")
    }
  }

  if (errors.length > 0) return { ok: false, errors }
  return { ok: true, pkg: input as unknown as ThemePackage }
}

// ─── Pure planners ───────────────────────────────────────────────────────────

export interface SeedSectionRow {
  section_type: string
  order_index: number
  content: Record<string, unknown>
}

export interface SeedPageRow {
  slug: string
  title: string
  page_type: string
  is_published: boolean
  sections: SeedSectionRow[]
}

/** Section content = registry default, overridden by package content, plus variant. */
function sectionContent(section: ThemePackagePage["sections"][number]): Record<string, unknown> {
  const defaults = getDefaultBlockContent(section.type as BlockType) as Record<string, unknown>
  const base = defaults && typeof defaults === "object" ? defaults : {}
  const merged: Record<string, unknown> = { ...base, ...(section.content ?? {}) }
  if (section.variant !== undefined) merged.variant = section.variant
  return merged
}

/** Expand a package into the page/section rows signup seeding inserts. */
export function buildSeedRows(pkg: ThemePackage): SeedPageRow[] {
  return pkg.pages.map((page) => ({
    slug: page.slug,
    title: page.title,
    page_type: page.page_type,
    is_published: page.is_published ?? true,
    sections: page.sections.map((section, i) => ({
      section_type: section.type,
      order_index: i,
      content: sectionContent(section),
    })),
  }))
}

export interface ExistingSection {
  id: string
  section_type: string
  content: Record<string, unknown> | null
}

export interface SectionVariantUpdate {
  id: string
  content: Record<string, unknown>
}

/**
 * Plan the section updates for switching an existing site to a theme.
 * Only the `variant` key ever changes; all other content is preserved
 * verbatim. Sections whose type the package doesn't style are left alone.
 * The package's variant per type is its first occurrence across pages.
 */
export function planThemeApplication(
  pkg: ThemePackage,
  existing: ExistingSection[]
): SectionVariantUpdate[] {
  const variantByType = new Map<string, string>()
  for (const page of pkg.pages) {
    for (const section of page.sections) {
      if (section.variant !== undefined && !variantByType.has(section.type)) {
        variantByType.set(section.type, section.variant)
      }
    }
  }

  const updates: SectionVariantUpdate[] = []
  for (const section of existing) {
    const variant = variantByType.get(section.section_type)
    if (variant === undefined) continue
    const current = section.content ?? {}
    if (current.variant === variant) continue
    updates.push({ id: section.id, content: { ...current, variant } })
  }
  return updates
}

// ─── Supabase wrappers ───────────────────────────────────────────────────────

export interface ThemeRow {
  id: string
  name: string
  preview_image_url: string | null
  cause_areas: string[]
  package: ThemePackage
}

/** Active, valid theme packages for the gallery. Invalid packages are logged and skipped. */
export async function fetchActiveThemes(): Promise<ThemeRow[]> {
  const { data, error } = await supabase
    .from("templates")
    .select("id, name, preview_image_url, cause_areas, package")
    .eq("is_active", true)
    .not("package", "is", null)
    .order("name")
  if (error || !data) return []

  const rows: ThemeRow[] = []
  for (const row of data) {
    const result = validateThemePackage(row.package)
    if (result.ok) {
      rows.push({
        id: row.id,
        name: row.name,
        preview_image_url: row.preview_image_url,
        cause_areas: (row.cause_areas as string[] | null) ?? [],
        package: result.pkg,
      })
    } else {
      console.error(`Theme "${row.name}" (${row.id}) failed validation:`, result.errors)
    }
  }
  return rows
}

/** Persist package tokens onto org_customizations, keeping legacy columns in sync. */
async function applyThemeTokens(orgId: string, tokens: StoredDesignTokens | undefined, templateId?: string): Promise<Error | null> {
  if (!tokens && !templateId) return null
  const design_tokens = tokens ?? {}
  const legacyCols = tokens ? legacyColumnsFromTokens(design_tokens) : {}
  const { error } = await supabase.from("org_customizations").upsert(
    {
      org_id: orgId,
      ...legacyCols,
      ...(tokens ? { design_tokens: design_tokens as Json } : {}),
      ...(templateId ? { template_id: templateId } : {}),
    },
    { onConflict: "org_id" }
  )
  return error ? (error as unknown as Error) : null
}

/**
 * Seed a new org's pages from a theme package. Idempotent: no-op when the org
 * already has pages. Also applies the package's tokens.
 */
export async function applyThemeAtSignup(
  orgId: string,
  pkg: ThemePackage,
  templateId?: string
): Promise<Error | null> {
  const { data: existing } = await supabase
    .from("pages")
    .select("id")
    .eq("org_id", orgId)
    .limit(1)
  if (existing && existing.length > 0) return null

  for (const pageRow of buildSeedRows(pkg)) {
    const { data: page, error: pageError } = await supabase
      .from("pages")
      .insert({
        org_id: orgId,
        slug: pageRow.slug,
        title: pageRow.title,
        page_type: pageRow.page_type,
        is_published: pageRow.is_published,
      })
      .select("id")
      .single()
    if (pageError || !page) return pageError as unknown as Error

    for (const section of pageRow.sections) {
      const { error: sectionError } = await supabase.from("page_sections").insert({
        page_id: page.id,
        org_id: orgId,
        section_type: section.section_type,
        order_index: section.order_index,
        content: section.content as Json,
        is_visible: true,
      })
      if (sectionError) return sectionError as unknown as Error
    }
  }

  return applyThemeTokens(orgId, pkg.tokens, templateId)
}

/**
 * NOS-158: apply a theme's per-type layout variants to an org's sections,
 * optionally excluding sections another code path already updated (the
 * builder store handles the current page itself so those edits are undoable).
 */
export async function applyThemeVariants(
  orgId: string,
  pkg: ThemePackage,
  opts?: { excludeSectionIds?: string[] }
): Promise<Error | null> {
  const { data: sections, error: fetchError } = await supabase
    .from("page_sections")
    .select("id, section_type, content")
    .eq("org_id", orgId)
  if (fetchError) return fetchError as unknown as Error

  const excluded = new Set(opts?.excludeSectionIds ?? [])
  const candidates = ((sections ?? []) as ExistingSection[]).filter((s) => !excluded.has(s.id))
  const updates = planThemeApplication(pkg, candidates)
  for (const update of updates) {
    const { error } = await supabase
      .from("page_sections")
      .update({ content: update.content as Json })
      .eq("id", update.id)
    if (error) return error as unknown as Error
  }
  return null
}

